import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, Search, BarChart3, ChevronDown, ChevronRight, Filter } from 'lucide-react';

export default function GameRecordsPage() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterTenant, setFilterTenant] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [providers, setProviders] = useState([]);
  const [tenants, setTenants] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [recRes, provRes, tenRes] = await Promise.all([
        api.get('/api/admin/game-records').catch(() => ({ data: { records: [], summary: null } })),
        api.get('/api/admin/providers').catch(() => ({ data: { data: { items: [] } } })),
        api.get('/api/admin/tenants').catch(() => ({ data: { items: [] } })),
      ]);
      setRecords(recRes.data.records || []);
      setSummary(recRes.data.summary || null);
      setProviders(provRes.data.data?.items || []);
      setTenants(tenRes.data.tenants || tenRes.data.items || []);
    } catch {}
    finally { setLoading(false); }
  };

  const syncRecords = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/api/admin/game-records/sync');
      toast.success(`Synced! ${res.data.synced_accounts || 0} balances, ${res.data.records_fetched || 0} game records`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const filtered = records.filter(r => {
    if (filterProvider !== 'all' && r.provider_code !== filterProvider) return false;
    if (filterTenant !== 'all') {
      if (filterTenant === 'main' && r.tenant_id) return false;
      if (filterTenant !== 'main' && r.tenant_id !== filterTenant) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return (r.username || '').toLowerCase().includes(s) || (r.player_email || '').toLowerCase().includes(s);
    }
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="game-records-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Game Records</h2>
          <p className="text-sm text-gray-400">Players' activity across providers, tenants & inner games (fish, slots, etc.)</p>
        </div>
        <Button onClick={syncRecords} disabled={syncing} className="bg-[#D4AF37] text-black" data-testid="sync-records-btn">
          {syncing ? <div className="spinner w-4 h-4 mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Sync All Records
        </Button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard label="Total Accounts" value={summary.total_accounts} />
          <SummaryCard label="Total Game $ (Provider)" value={`$${(summary.total_balance || 0).toFixed(2)}`} gold />
          <SummaryCard label="Total Loaded ($)" value={`$${(summary.total_loaded || 0).toFixed(2)}`} color="text-green-400" />
          <SummaryCard label="Total Redeemed ($)" value={`$${(summary.total_redeemed || 0).toFixed(2)}`} color="text-red-400" />
        </div>
      )}

      {/* Per-Provider + Per-Tenant */}
      {summary?.per_provider && Object.keys(summary.per_provider).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Spend by Provider & Group</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(summary.per_provider).map(([code, data]) => (
              <div key={code} className="bg-[#131822] rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold capitalize text-[#D4AF37]">{code}</h4>
                  <span className="text-xs text-gray-500">{data.accounts} players</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <div><p className="text-gray-500">Balance</p><p className="text-[#D4AF37] font-bold">${(data.balance || 0).toFixed(2)}</p></div>
                  <div><p className="text-gray-500">Loaded</p><p className="text-green-400 font-bold">${(data.loaded || 0).toFixed(2)}</p></div>
                  <div><p className="text-gray-500">Redeemed</p><p className="text-red-400 font-bold">${(data.redeemed || 0).toFixed(2)}</p></div>
                </div>
                {data.by_tenant && Object.keys(data.by_tenant).length > 0 && (
                  <div className="border-t border-white/5 pt-2">
                    <p className="text-[10px] text-gray-500 mb-1 font-medium">BY GROUP / TENANT</p>
                    {Object.entries(data.by_tenant).map(([tid, td]) => (
                      <div key={tid} className="flex items-center justify-between text-[11px] py-0.5">
                        <span className="text-gray-400">{tid === 'main' ? 'Main Platform' : tid}</span>
                        <div className="flex gap-3">
                          <span className="text-[#D4AF37]">${(td.balance || 0).toFixed(2)}</span>
                          <span className="text-green-400">${(td.loaded || 0).toFixed(2)}</span>
                          <span className="text-red-400">${(td.redeemed || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search player, username..." className="pl-10 bg-[#131822] border-white/10" />
        </div>
        <select value={filterProvider} onChange={e => setFilterProvider(e.target.value)} className="bg-[#131822] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="all">All Providers</option>
          {providers.map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
        </select>
        <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} className="bg-[#131822] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="all">All Groups</option>
          <option value="main">Main Platform</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {/* Records Table */}
      <div className="bg-[#131822] rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-xs text-gray-500">
              <th className="w-8 p-3"></th>
              <th className="text-left p-3">Player</th>
              <th className="text-left p-3">Provider</th>
              <th className="text-left p-3">Game Username</th>
              <th className="text-right p-3">Balance ($)</th>
              <th className="text-right p-3">Loaded ($)</th>
              <th className="text-right p-3">Redeemed ($)</th>
              <th className="text-left p-3">Group</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <React.Fragment key={i}>
                <tr className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer" onClick={() => setExpandedRow(expandedRow === i ? null : i)}>
                  <td className="p-3 text-gray-500">
                    {r.inner_records ? (expandedRow === i ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="w-3.5" />}
                  </td>
                  <td className="p-3"><span className="text-white">{r.player_email || '?'}</span></td>
                  <td className="p-3"><span className="text-[#D4AF37] capitalize font-medium">{r.provider_code}</span></td>
                  <td className="p-3 font-mono text-xs">{r.username}</td>
                  <td className="p-3 text-right font-semibold text-[#D4AF37]">${(r.balance || 0).toFixed(2)}</td>
                  <td className="p-3 text-right text-green-400">${(r.total_loaded || 0).toFixed(2)}</td>
                  <td className="p-3 text-right text-red-400">${(r.total_redeemed || 0).toFixed(2)}</td>
                  <td className="p-3 text-xs text-gray-400">{r.tenant_name || 'Main'}</td>
                </tr>
                {/* Expanded: Inner Game Records (fish, slots, etc inside this provider) */}
                {expandedRow === i && (
                  <tr><td colSpan={8} className="p-0">
                    <div className="bg-[#0B0E14] px-6 py-4 border-y border-white/5">
                      <h4 className="text-xs font-semibold text-[#D4AF37] mb-3">Inner Game Activity - {r.provider_code} / {r.username}</h4>
                      {r.inner_records?.summary && (
                        <div className="grid grid-cols-4 gap-3 mb-3 text-xs">
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Total Played</p><p className="text-white font-bold">${(r.inner_records.summary.sum_played || 0).toFixed(2)}</p></div>
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Total Won</p><p className="text-green-400 font-bold">${(r.inner_records.summary.sum_win || 0).toFixed(2)}</p></div>
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Net Win/Loss</p><p className={`font-bold ${(r.inner_records.summary.sum_win_lose || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>${(r.inner_records.summary.sum_win_lose || 0).toFixed(2)}</p></div>
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Records</p><p className="text-white font-bold">{r.inner_records.count || 0}</p></div>
                        </div>
                      )}
                      {r.inner_records?.records?.length > 0 ? (
                        <div className="bg-[#131822] rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead><tr className="text-gray-500 border-b border-white/5 bg-[#131822]">
                              <th className="text-left p-2">Game ID</th>
                              <th className="text-left p-2">Account</th>
                              <th className="text-left p-2">Game Title</th>
                              <th className="text-right p-2">Bal Before</th>
                              <th className="text-right p-2">Total Played</th>
                              <th className="text-right p-2">Total Win</th>
                              <th className="text-right p-2">Bal Now</th>
                              <th className="text-left p-2">Time</th>
                            </tr></thead>
                            <tbody>
                              {r.inner_records.records.slice(0, 30).map((gr, gi) => (
                                <tr key={gi} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                                  <td className="p-2 font-mono text-gray-400">{gr.game_id || gr.id || '-'}</td>
                                  <td className="p-2">{gr.account || gr.user_id || r.username}</td>
                                  <td className="p-2 text-white font-medium">{gr.game_name || gr.game_title || gr.game_type || 'Unknown'}</td>
                                  <td className="p-2 text-right text-gray-400">${(gr.balance_before || gr.start_balance || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right text-orange-400">${(gr.total_played || gr.played || gr.bet || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right text-green-400">${(gr.total_win || gr.win || gr.won || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right text-[#D4AF37] font-semibold">${(gr.balance_now || gr.end_balance || gr.balance || 0).toFixed(2)}</td>
                                  <td className="p-2 text-gray-500">{gr.recording_time || gr.created_at || gr.timestamp || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-xs">
                          {r.inner_records ? 'No inner game records found for this player.' : 'Click "Sync All Records" to fetch inner game data from provider.'}
                        </div>
                      )}
                      {r.inner_records?.fetched_at && (
                        <p className="text-[10px] text-gray-600 mt-2">Last fetched: {new Date(r.inner_records.fetched_at).toLocaleString()}</p>
                      )}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500">No records. Click "Sync All Records" to fetch from providers.</div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, gold, color }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
      <p className="text-[10px] text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-bold ${gold ? 'text-[#D4AF37]' : color || 'text-white'}`}>{value}</p>
    </div>
  );
}
